import { useState } from 'react'
import toast from 'react-hot-toast'

const META_KEYS = ['source_url', 'date_collected', 'date_entered', 'status']

const formatFieldLabel = (key) => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const DataResults = ({ result, schema = null }) => {
  const [showMarkdown, setShowMarkdown] = useState(false)
  const [expandedRows, setExpandedRows] = useState({})

  // Use only schema or result data – no hardcoded Jugendamt columns
  const getSchemaFields = () => {
    const fromResult = () => {
      const keys = new Set()
      if (result?.results?.length) {
        result.results.forEach(r => {
          if (r?.data && typeof r.data === 'object') {
            Object.keys(r.data).filter(k => !META_KEYS.includes(k)).forEach(k => keys.add(k))
          }
        })
      } else if (result?.data && typeof result.data === 'object') {
        Object.keys(result.data).filter(k => !META_KEYS.includes(k)).forEach(k => keys.add(k))
      }
      return keys.size ? Array.from(keys) : null
    }
    const fromSchema = schema && Object.keys(schema).length ? Object.keys(schema) : null
    return fromResult() || fromSchema || []
  }

  const getFieldName = (fieldName) => {
    return formatFieldLabel(fieldName)
  }
  
  const getFieldDescription = (fieldName) => {
    if (schema && schema[fieldName]) {
      return schema[fieldName].description || ''
    }
    return ''
  }
  
  const isFieldRequired = (fieldName) => {
    if (schema && schema[fieldName]) {
      return schema[fieldName].required || false
    }
    return false
  }
  
  const calculateScore = (data) => {
    const schemaFields = getSchemaFields()
    if (schemaFields.length === 0) return 0
    
    let filledCount = 0
    schemaFields.forEach(field => {
      const value = data[field]
      if (value && value !== '' && value !== '-') {
        filledCount++
      }
    })
    
    return Math.round((filledCount / schemaFields.length) * 100)
  }
  
  const getScoreColor = (score) => {
    return 'text-green-600 bg-green-50'
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!')
  }

  const downloadJSON = () => {
    if (!result) {
      toast.error('No data available')
      return
    }
    
    let exportData
    
    if (result.mode === 'batch' || result.mode === 'auto') {
      exportData = result.results
        ?.map(r => r.data)
        .filter(d => d) || []
    } else {
      exportData = result.data
    }
    
    if (!exportData || (Array.isArray(exportData) && exportData.length === 0)) {
      toast.error('No data to export')
      return
    }
    
    const dataStr = JSON.stringify(exportData, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `data_extracted_${Date.now()}.json`
    link.click()
  }

  const downloadCSV = () => {
    if (!result) {
      toast.error('No data available')
      return
    }
    
    const data = result.mode === 'batch' || result.mode === 'auto'
      ? result.results?.map(r => r.data).filter(d => d) || []
      : [result.data].filter(d => d)

    if (data.length === 0) {
      toast.error('No data to export')
      return
    }

    const schemaFields = getSchemaFields()
    const headers = [...schemaFields, 'source_url', 'date_collected', 'status']

    const rows = data.map(d => {
      const row = schemaFields.map(field => {
        const value = d[field]
        if (value && typeof value === 'string') {
          return value.includes(',') || value.includes('"') || value.includes('\n') 
            ? `"${value.replace(/"/g, '""')}"` 
            : value
        }
        return value || ''
      })
      row.push(d.source_url || '', d.date_collected || '', d.status || '')
      return row
    })

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `data_${Date.now()}.csv`
    link.click()
  }

  const toggleRow = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  if (!result.success) {
    return (
      <div className="card">
        <div className="flex items-center space-x-3 text-red-600 mb-4">
          <i className="fa-solid fa-circle-exclamation text-2xl"></i>
          <h3 className="text-xl font-bold">Extraction Failed</h3>
        </div>
        <p className="text-dark-100">{result.error}</p>
      </div>
    )
  }

  if (result.mode === 'batch' || result.mode === 'auto') {
    const successfulResults = result.results?.filter(r => r.success && r.data) || []
    const isAutoMode = result.mode === 'auto'
    
    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className={`card ${isAutoMode ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-l-primary-500' : 'bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-l-green-500'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full ${isAutoMode ? 'bg-primary-500' : 'bg-green-500'}`}>
                <i className={`fa-solid ${isAutoMode ? 'fa-search' : 'fa-check'} text-white text-xl`}></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-dark-300">
                  {isAutoMode ? 'Auto Search & Extraction Complete!' : 'Batch Processing Complete!'}
                </h3>
                <p className="text-sm text-dark-100">
                  {result.successful_extractions} successful / {result.total_processed} total
                  {result.processing_time && ` • ${result.processing_time.toFixed(1)}s`}
                </p>
                {isAutoMode && result.search_query && (
                  <p className="text-xs text-dark-100 mt-1">
                    Query: "{result.search_query}"
                  </p>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              <button onClick={downloadCSV} className="btn-secondary py-2 px-4 text-sm">
                <i className="fa-solid fa-download mr-1"></i>
                Export CSV
              </button>
              <button onClick={downloadJSON} className="btn-secondary py-2 px-4 text-sm">
                <i className="fa-solid fa-download mr-1"></i>
                Export JSON
              </button>
            </div>
          </div>
        </div>

        {/* Auto Mode: Show Found URLs */}
        {isAutoMode && result.urls_found && result.urls_found.length > 0 && (
          <div className="card bg-purple-50 border border-purple-200">
            <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
              <i className="fa-solid fa-link text-purple-600 mr-2"></i>
              URLs Found ({result.total_urls_found})
            </h4>
            <div className="max-h-40 overflow-y-auto bg-white rounded p-3">
              <ul className="text-sm space-y-1">
                {result.urls_found.map((url, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-purple-600 font-mono">#{index + 1}</span>
                    <a 
                      href={url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-primary-600 hover:underline break-all"
                    >
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Table View */}
        {successfulResults.length > 0 && (
          <div className="card overflow-hidden">
            <h3 className="text-xl font-bold text-dark-300 mb-4">Extracted Data ({successfulResults.length} Results)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-light-200/30">
                <thead className="bg-light-100">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-dark-100 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <span>Score</span>
                        <i className="fa-solid fa-info-circle text-light-200 cursor-help" title="Completeness: 0 = empty, 100 = all fields filled"></i>
                      </div>
                    </th>
                    {getSchemaFields().map(field => {
                      const description = getFieldDescription(field)
                      const isRequired = isFieldRequired(field)
                      
                      return (
                        <th key={field} className="px-3 py-3 text-left text-xs font-medium text-dark-100 uppercase tracking-wider">
                          <div className="flex items-center gap-1">
                            <span>{getFieldName(field)}</span>
                            {isRequired && <span className="text-red-500">*</span>}
                            {description && (
                              <i className="fa-solid fa-info-circle text-light-200 cursor-help" title={description}></i>
                            )}
                          </div>
                        </th>
                      )
                    })}
                    <th className="px-3 py-3 text-left text-xs font-medium text-dark-100 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-dark-100 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-light-200/20">
                  {successfulResults.map((item, index) => (
                    <>
                      <tr key={index} className="hover:bg-light-100">
                        <td className="px-3 py-4 text-sm">
                          {item.data ? (
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full font-bold text-sm ${getScoreColor(calculateScore(item.data))}`}>
                                {calculateScore(item.data)}
                              </span>
                              <div className="w-16 bg-light-100 rounded-full h-2">
                                <div 
                                  className="h-2 rounded-full bg-green-500"
                                  style={{ width: `${calculateScore(item.data)}%` }}
                                />
                              </div>
                            </div>
                          ) : '-'}
                        </td>
                        {getSchemaFields().map(field => {
                          const value = item.data[field] || '-'
                          if (field === 'telephone' && value !== '-') {
                            return (
                              <td key={field} className="px-3 py-4 text-sm">
                                <a href={`tel:${value}`} className="text-primary-600 hover:text-primary-800">
                                  {value}
                                </a>
                              </td>
                            )
                          }
                          if (field === 'email' && value !== '-') {
                            return (
                              <td key={field} className="px-3 py-4 text-sm">
                                <a href={`mailto:${value}`} className="text-primary-600 hover:text-primary-800">
                                  {value}
                                </a>
                              </td>
                            )
                          }
                          if ((field === 'website' || field === 'source_url') && value !== '-') {
                            return (
                              <td key={field} className="px-3 py-4 text-sm">
                                <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  🔗 Link
                                </a>
                              </td>
                            )
                          }
                          return (
                            <td key={field} className="px-3 py-4 text-sm text-dark-300">
                              {value}
                            </td>
                          )
                        })}
                        <td className="px-3 py-4 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.data.status === 'verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {item.data.status || 'pending'}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-sm">
                          <button
                            onClick={() => toggleRow(index)}
                            className="text-primary-600 hover:text-primary-800 font-medium"
                          >
                            {expandedRows[index] ? 'Hide' : 'Show'}
                          </button>
                        </td>
                      </tr>
                      {expandedRows[index] && (
                        <tr key={`${index}-expanded`}>
                          <td colSpan="8" className="px-3 py-4 bg-light-100/50">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              {getSchemaFields().slice(0, 10).map(field => (
                                <div key={field}>
                                  <p className="text-xs text-dark-100">{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                                  <p className="font-medium text-dark-300">{item.data[field] || '-'}</p>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Failed Extractions */}
        {result.failed_extractions > 0 && (
          <div className="card bg-red-50 border border-red-200">
            <h4 className="font-semibold text-red-900 mb-2">Failed Extractions ({result.failed_extractions})</h4>
            <div className="space-y-2">
              {result.results?.filter(r => !r.success).map((item, index) => (
                <div key={index} className="text-sm text-red-800 bg-white p-2 rounded">
                  <p className="font-medium">Error: {item.error}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Single result
  const data = result.data

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="card bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-l-green-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-green-500 p-2 rounded-full">
              <i className="fa-solid fa-check text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-dark-300">Data Extracted Successfully!</h3>
              <p className="text-sm text-dark-100">
                Processing time: {result.processing_time?.toFixed(2)}s
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button onClick={downloadCSV} className="btn-secondary py-2 px-4 text-sm">
              <i className="fa-solid fa-download mr-1"></i>
              Export CSV
            </button>
            <button onClick={downloadJSON} className="btn-secondary py-2 px-4 text-sm">
              <i className="fa-solid fa-download mr-1"></i>
              Export JSON
            </button>
          </div>
        </div>
      </div>

      {/* Data Display - dynamic so custom template fields (e.g. bank_name) always show */}
      <div className="card">
        <h3 className="text-xl font-bold text-dark-300 mb-4">Extracted Information</h3>
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(data)
                .filter(([key]) => !META_KEYS.includes(key))
                .filter(([, value]) => value != null && value !== '' && value !== '-')
                .map(([key, value]) => (
                  <div key={key} className="p-4 bg-light-100 rounded-lg border border-light-200/30">
                    <p className="text-xs text-dark-100 font-semibold mb-1 uppercase">{formatFieldLabel(key)}</p>
                    {key === 'telephone' && value ? (
                      <a href={`tel:${value}`} className="text-sm font-medium text-primary-600 hover:text-primary-800">{value}</a>
                    ) : key === 'email' && value ? (
                      <a href={`mailto:${value}`} className="text-sm font-medium text-primary-600 hover:text-primary-800">{value}</a>
                    ) : (key === 'website' || key.includes('url')) && value ? (
                      <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:text-primary-800 break-all">{value}</a>
                    ) : (
                      <p className="text-sm font-medium text-dark-300 whitespace-pre-wrap">{String(value)}</p>
                    )}
                  </div>
                ))}
            </div>

            {/* Metadata */}
            <div className="border-t border-light-200/30 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                <div>
                  <p className="text-dark-100">Source URL</p>
                  <a href={data.source_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all">
                    {data.source_url}
                  </a>
                </div>
                <div>
                  <p className="text-dark-100">Date Collected</p>
                  <p className="font-medium text-dark-300">{data.date_collected}</p>
                </div>
                <div>
                  <p className="text-dark-100">Date Data Entered</p>
                  <p className="font-medium text-dark-300">{data.date_entered || data.date_collected}</p>
                </div>
                <div>
                  <p className="text-dark-100">Verification Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    data.status === 'verified' ? 'bg-green-100 text-green-800' : 
                    data.status === 'verification_pending' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {data.status === 'verification_pending' ? 'Verification Pending' : data.status || 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Raw Markdown (Optional) */}
      {result.raw_markdown && (
        <div className="card">
          <button
            onClick={() => setShowMarkdown(!showMarkdown)}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-semibold text-dark-300">Raw Markdown Content</h3>
            <i className={`fa-solid fa-chevron-${showMarkdown ? 'up' : 'down'} text-light-200`}></i>
          </button>
          
          {showMarkdown && (
            <div className="mt-4">
              <div className="bg-dark-300 text-white p-4 rounded-lg overflow-x-auto max-h-96 relative">
                <button
                  onClick={() => copyToClipboard(result.raw_markdown)}
                  className="absolute top-2 right-2 bg-dark-200 hover:bg-dark-100 text-white px-3 py-1 rounded text-xs"
                >
                  Copy
                </button>
                <pre className="text-sm whitespace-pre-wrap">{result.raw_markdown}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DataResults
